import { createContext } from 'preact'
import { useContext, useState, useCallback, useMemo } from 'preact/hooks'
import { getHeaderFilterDefaults } from '../content'
import { userDataService, lookupService } from '../functions/apis'
import type { PeriodMode } from '../functions/calculations'
import type { Client, ClientUnit, Unit, Currency } from '../types/api'

interface FilterContextType {
  // Current selections
  selectedClient: Client | null
  selectedUnit: ClientUnit | null
  selectedUnitDetails: Unit | null
  selectedCurrency: Currency | null
  periodMode: PeriodMode
  
  // Available options
  clients: Client[]
  clientUnits: ClientUnit[]
  allUnits: Unit[]
  currencies: Currency[]
  
  // Loading states
  loading: boolean
  error: string | null
  
  // Actions
  setClient: (client: Client) => Promise<void>
  setUnit: (unit: ClientUnit) => void
  setCurrency: (currency: Currency | null) => void
  setPeriodMode: (mode: PeriodMode) => void
  initializeFilters: () => Promise<void>
  clearFilters: () => void
}

const FilterContext = createContext<FilterContextType | null>(null)
const filterDefaults = getHeaderFilterDefaults()

function findDefaultCurrency(currencies: Currency[]): Currency | null {
  return (
    currencies.find((currency) => currency.currency?.toUpperCase() === filterDefaults.currency) ??
    null
  )
}

export function FilterProvider({ children }: { children: preact.ComponentChildren }) {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [selectedUnit, setSelectedUnit] = useState<ClientUnit | null>(null)
  const [selectedUnitDetails, setSelectedUnitDetails] = useState<Unit | null>(null)
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null)
  const [periodMode, setPeriodMode] = useState<PeriodMode>(filterDefaults.period)
  const [clients, setClients] = useState<Client[]>([])
  const [clientUnits, setClientUnits] = useState<ClientUnit[]>([])
  const [allUnits, setAllUnits] = useState<Unit[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)

  const initializeFilters = useCallback(async () => {
    // Prevent multiple initializations
    if (initialized || loading) {
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      const [userData, currenciesData, unitsData] = await Promise.all([
        userDataService.initializeUserData(),
        lookupService.getCurrencies().catch(() => []),
        lookupService.getUnits().catch(() => []),
      ])
      
      setClients(userData.clients)
      setSelectedClient(userData.selectedClient)
      setClientUnits(userData.clientUnits)
      setSelectedUnit(userData.selectedUnit)
      setCurrencies(currenciesData)
      setAllUnits(unitsData)
      setSelectedUnitDetails(
        unitsData.find((unit) => unit.unit_id_no === userData.selectedUnit?.unit_id_no) || null,
      )
      
      const defaultCurrency = findDefaultCurrency(currenciesData)
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency)
      }
      
      setInitialized(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize filters')
      console.error('Error initializing filters:', err)
    } finally {
      setLoading(false)
    }
  }, [initialized, loading])

  const setClient = useCallback(async (client: Client) => {
    setLoading(true)
    setError(null)
    
    try {
      const updatedData = await userDataService.switchClient(client)
      
      setSelectedClient(updatedData.selectedClient!)
      setClientUnits(updatedData.clientUnits!)
      if (updatedData.selectedUnit) {
        setSelectedUnit(updatedData.selectedUnit)
        
        // Fetch full unit details for the first unit
        if (updatedData.selectedUnit.unit_id_no) {
          const unitDetails = allUnits.find(u => u.unit_id_no === updatedData.selectedUnit!.unit_id_no)
          setSelectedUnitDetails(unitDetails || null)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch client')
      console.error('Error switching client:', err)
    } finally {
      setLoading(false)
    }
  }, [allUnits])

  const setUnit = useCallback(async (unit: ClientUnit) => {
    try {
      const updatedData = userDataService.switchUnit(unit)
      if (updatedData.selectedUnit) {
        setSelectedUnit(updatedData.selectedUnit)
      }
      
      // Fetch full unit details
      if (unit.unit_id_no) {
        const unitDetails = allUnits.find(u => u.unit_id_no === unit.unit_id_no)
        setSelectedUnitDetails(unitDetails || null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch unit')
      console.error('Error switching unit:', err)
    }
  }, [allUnits])

  const setCurrency = useCallback((currency: Currency | null) => {
    setSelectedCurrency(currency)
  }, [])

  const updatePeriodMode = useCallback((mode: PeriodMode) => {
    setPeriodMode(mode)
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedClient(null)
    setSelectedUnit(null)
    setSelectedUnitDetails(null)
    setClientUnits([])
    setSelectedCurrency(findDefaultCurrency(currencies))
    setPeriodMode(filterDefaults.period)
    setError(null)
  }, [currencies])

  const contextValue = useMemo(() => ({
    selectedClient,
    selectedUnit,
    selectedUnitDetails,
    selectedCurrency,
    periodMode,
    clients,
    clientUnits,
    allUnits,
    currencies,
    loading,
    error,
    setClient,
    setUnit,
    setCurrency,
    setPeriodMode: updatePeriodMode,
    initializeFilters,
    clearFilters
  }), [
    selectedClient,
    selectedUnit,
    selectedUnitDetails,
    selectedCurrency,
    periodMode,
    clients,
    clientUnits,
    allUnits,
    currencies,
    loading,
    error,
    setClient,
    setUnit,
    setCurrency,
    updatePeriodMode,
    initializeFilters,
    clearFilters
  ])

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  )
}

export const useFilter = () => {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider')
  }
  return context
}
